import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';

export default function AdminUsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [models, setModels] = useState([]);
  const [globalDefault, setGlobalDefault] = useState(null);
  const [error, setError] = useState(null);
  const [rowStatus, setRowStatus] = useState({}); // user-id → 'gespeichert' | Fehlermeldung
  const [savingRows, setSavingRows] = useState({}); // user-id → true während PATCH läuft
  const statusTimers = useRef({}); // user-id → timeoutId

  useEffect(() => {
    api.get('/admin/users')
      .then((r) => setUsers(r.data))
      .catch(() => setError('Laden fehlgeschlagen'));
    api.get('/models').then((r) => setModels(r.data.models)).catch(() => {});
    api.get('/settings/default_model')
      .then((r) => setGlobalDefault(r.data.value))
      .catch(() => {}); // 404 = kein Setting, Label bleibt "Global"
  }, []);

  const patch = async (id, field, value) => {
    if (statusTimers.current[id]) {
      clearTimeout(statusTimers.current[id]);
      delete statusTimers.current[id];
    }
    setRowStatus((s) => ({ ...s, [id]: null }));
    setSavingRows((s) => ({ ...s, [id]: true }));
    try {
      const r = await api.patch(`/admin/users/${id}`, { [field]: value });
      setUsers((list) => list.map((u) => (u.id === id ? r.data : u)));
      setRowStatus((s) => ({ ...s, [id]: 'gespeichert' }));
      statusTimers.current[id] = setTimeout(() => {
        setRowStatus((s) => ({ ...s, [id]: null }));
        delete statusTimers.current[id];
      }, 2000);
    } catch (e) {
      setRowStatus((s) => ({
        ...s,
        [id]: e.response?.data?.error || 'Speichern fehlgeschlagen',
      }));
    } finally {
      setSavingRows((s) => ({ ...s, [id]: false }));
    }
  };

  return (
    <div className="admin">
      <Link to="/">← Zurück</Link>
      <h1>Benutzer verwalten</h1>

      {error && <div className="error">{error}</div>}

      <p className="meta">
        Änderungen an Rolle und Modell-Wahl greifen erst nach erneutem Login des Users.
        Das Default-Modell wirkt sofort.
      </p>

      <table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Rolle</th>
            <th>Aktiv</th>
            <th>Modell-Wahl</th>
            <th>Default-Modell</th>
            <th>Letzter Login</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {users.length === 0 && (
            <tr><td colSpan={8} className="meta">Keine Benutzer.</td></tr>
          )}
          {users.map((u) => {
            const isSelf = u.id === me.id;
            const saving = !!savingRows[u.id];
            return (
              <tr key={u.id}>
                <td>{u.display_name}</td>
                <td>{u.email}</td>
                <td>
                  <select
                    value={u.role}
                    disabled={isSelf || saving}
                    onChange={(e) => patch(u.id, 'role', e.target.value)}
                  >
                    <option value="user">Benutzer</option>
                    <option value="admin">Admin</option>
                  </select>
                </td>
                <td>
                  <input
                    type="checkbox"
                    checked={u.is_active}
                    disabled={isSelf || saving}
                    onChange={(e) => patch(u.id, 'is_active', e.target.checked)}
                  />
                </td>
                <td>
                  <input
                    type="checkbox"
                    checked={u.role === 'admin' || u.can_choose_model}
                    disabled={u.role === 'admin' || saving}
                    title={u.role === 'admin' ? 'Admins dürfen implizit Modelle wählen' : ''}
                    onChange={(e) => patch(u.id, 'can_choose_model', e.target.checked)}
                  />
                </td>
                <td>
                  <select
                    value={u.default_model || ''}
                    disabled={saving}
                    onChange={(e) => patch(u.id, 'default_model', e.target.value || null)}
                  >
                    <option value="">
                      Global{globalDefault ? ` (${globalDefault})` : ''}
                    </option>
                    {models.map((m) => (
                      <option key={m.id} value={m.id}>{m.label}</option>
                    ))}
                  </select>
                </td>
                <td>
                  {u.last_login_at
                    ? new Date(u.last_login_at).toLocaleDateString('de-DE')
                    : '—'}
                </td>
                <td className={rowStatus[u.id] ? (rowStatus[u.id] === 'gespeichert' ? 'meta' : 'error') : undefined}>
                  {rowStatus[u.id] || ''}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
